import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    ReactNode,
} from 'react';
import { useAutoSave, type AutoSaveStatus } from '../../hooks/useAutoSave';
import { ActionResult, DocumentStatus } from './types';

interface DocumentData {
    [key: string]: any;
}

interface PersistResult {
    documentId?: string;
    status?: DocumentStatus;
}

export interface AutoSaveConfig {
    enabled: boolean;
    debounceMs?: number;
    onError?: (error: string) => void;
}

interface DocumentContextType {
    data: DocumentData;
    updateData: (key: string, value: any) => void;
    setValue: (key: string, value: any) => void;
    save: () => Promise<ActionResult>;
    complete: () => Promise<ActionResult>;
    isSaving: boolean;
    isLoading: boolean;
    documentId?: string;
    documentStatus?: DocumentStatus;
    autoSaveStatus: AutoSaveStatus;
    lastSavedAt: Date | null;
}

interface DocumentProviderProps {
    children: ReactNode;
    initialData?: DocumentData;
    documentId?: string;
    documentData?: { data?: DocumentData; status?: DocumentStatus };
    onDataChange?: (data: DocumentData) => void;
    autoSave?: AutoSaveConfig;
    saveDocument: (
        data: DocumentData,
        documentId?: string
    ) => Promise<PersistResult>;
    completeDocument: (
        data: DocumentData,
        documentId?: string
    ) => Promise<PersistResult>;
}

const DocumentContext = createContext<DocumentContextType | undefined>(undefined);

const hasKeys = (value?: DocumentData): value is DocumentData =>
    !!value && Object.keys(value).length > 0;

const mergeIfChanged = (
    current: DocumentData,
    incoming?: DocumentData
): DocumentData => {
    if (!hasKeys(incoming)) {
        return current;
    }

    let changed = false;
    const next: DocumentData = { ...current };

    Object.entries(incoming).forEach(([key, value]) => {
        if (next[key] !== value) {
            next[key] = value;
            changed = true;
        }
    });

    return changed ? next : current;
};

export const DocumentProvider: React.FC<DocumentProviderProps> = ({
    children,
    initialData = {},
    documentId: initialDocumentId,
    documentData,
    onDataChange,
    autoSave: autoSaveConfig,
    saveDocument,
    completeDocument,
}) => {
    const mergedInitialData = useMemo(
        () => (documentData?.data ? { ...initialData, ...documentData.data } : initialData),
        [documentData?.data, initialData]
    );

    const [data, setData] = useState<DocumentData>(mergedInitialData);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading] = useState(false);
    const [currentDocumentId, setCurrentDocumentId] = useState<string | undefined>(
        initialDocumentId
    );
    const [documentStatus, setDocumentStatus] = useState<DocumentStatus | undefined>(
        documentData?.status
    );
    const hasHydratedInitialDataRef = useRef(hasKeys(mergedInitialData));
    const onDataChangeRef = useRef(onDataChange);

    useEffect(() => {
        onDataChangeRef.current = onDataChange;
    }, [onDataChange]);

    useEffect(() => {
        if (documentData?.data) {
            setData((prev) => mergeIfChanged(prev, documentData.data));
            setDocumentStatus((prev) =>
                prev === documentData.status ? prev : documentData.status
            );
        }
    }, [documentData?.data, documentData?.status]);

    useEffect(() => {
        if (documentData || hasHydratedInitialDataRef.current || !hasKeys(initialData)) {
            return;
        }

        setData((prev) => mergeIfChanged(prev, initialData));
        hasHydratedInitialDataRef.current = true;
    }, [initialData, documentData]);

    useEffect(() => {
        onDataChangeRef.current?.(data);
    }, [data]);

    const updateData = (key: string, value: any) => {
        setData((prevData) => {
            if (prevData[key] === value) {
                return prevData;
            }

            return {
                ...prevData,
                [key]: value,
            };
        });
    };

    const setValue = updateData;

    const saveFn = async (): Promise<ActionResult> => {
        setIsSaving(true);
        try {
            const result = await saveDocument(data, currentDocumentId);
            if (result.documentId) {
                setCurrentDocumentId(result.documentId);
            }
            if (result.status) {
                setDocumentStatus(result.status);
            }
            return { success: true };
        } catch (error: any) {
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                'Failed to save document';
            return { success: false, error: errorMessage };
        } finally {
            setIsSaving(false);
        }
    };

    const complete = async (): Promise<ActionResult> => {
        setIsSaving(true);
        try {
            const result = await completeDocument(data, currentDocumentId);
            if (result.documentId) {
                setCurrentDocumentId(result.documentId);
            }
            setDocumentStatus(result.status || 'completed');
            return { success: true };
        } catch (error: any) {
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                'Failed to complete document';
            return { success: false, error: errorMessage };
        } finally {
            setIsSaving(false);
        }
    };

    const { autoSaveStatus, lastSavedAt, cancelPendingAutoSave } = useAutoSave({
        save: saveFn,
        data,
        enabled: autoSaveConfig?.enabled ?? false,
        debounceMs: autoSaveConfig?.debounceMs,
        onError: autoSaveConfig?.onError,
    });

    const save = async (): Promise<ActionResult> => {
        cancelPendingAutoSave();
        return saveFn();
    };

    return (
        <DocumentContext.Provider
            value={{
                data,
                updateData,
                setValue,
                save,
                complete,
                isSaving,
                isLoading,
                documentId: currentDocumentId,
                documentStatus,
                autoSaveStatus,
                lastSavedAt,
            }}
        >
            {children}
        </DocumentContext.Provider>
    );
};

export const useDocument = () => {
    const context = useContext(DocumentContext);
    if (context === undefined) {
        throw new Error('useDocument must be used within a DocumentProvider');
    }
    return context;
};

export type { DocumentContextType };
